import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { ChartLineUp, TrendUp, Users, Target, PaperPlaneTilt, ChatCircleText, CheckCircle, XCircle, HandWaving } from "@phosphor-icons/react";

const cents = (c) => (c == null ? "—" : money(c));

export default function AdminRevenue() {
  const { data, isLoading } = useQuery({
    queryKey: ["ai-sales-analytics"],
    queryFn: async () => (await api.get("/ai-sales/analytics")).data,
    staleTime: 30000,
  });

  if (isLoading) return <div className="py-16 text-center text-muted-foreground" data-testid="revenue-loading">Loading…</div>;
  if (!data) return null;

  const f = data.funnel, r = data.revenue;
  const funnelSteps = [
    { k: "prospects_found", label: "Prospects found", icon: Users },
    { k: "leads_captured", label: "Leads captured", icon: Users },
    { k: "qualified_leads", label: "Qualified", icon: Target },
    { k: "outreach", label: "Outreach", icon: PaperPlaneTilt },
    { k: "conversations", label: "Conversations", icon: ChatCircleText },
    { k: "opportunities", label: "Opportunities", icon: TrendUp },
    { k: "conversions", label: "Conversions", icon: CheckCircle },
  ];
  const maxF = Math.max(1, ...funnelSteps.map((s) => f[s.k] || 0));
  const maxRev = Math.max(1, ...data.series.map((s) => s.revenue_cents));

  return (
    <div className="space-y-6" data-testid="admin-revenue">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight">
          <ChartLineUp size={28} weight="fill" /> Revenue & Analytics
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          What the autonomous SI Sales engine is producing. Only verified, completed conversions count as revenue.
          <span className="ml-1 font-medium text-foreground">Tenant: {data.tenant_id}</span>
        </p>
      </div>

      {/* headline revenue */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="revenue-cards">
        <Big label="SI-attributed revenue" value={cents(r.revenue_cents)} accent />
        <Big label="Deals won" value={f.deals_won} />
        <Big label="Conversion rate" value={`${r.conversion_rate}%`} />
        <Big label="Cross-sell revenue" value={cents(r.cross_sell_cents)} />
      </div>

      {/* 14-day revenue */}
      <Card title="Revenue — last 14 days">
        <div className="flex items-end gap-1.5" style={{ height: 120 }} data-testid="revenue-series">
          {data.series.map((s) => (
            <div key={s.date} className="group flex flex-1 flex-col items-center justify-end" title={`${s.date}: ${cents(s.revenue_cents)}`}>
              <div className="w-full rounded-t bg-primary/70 transition-colors group-hover:bg-primary"
                style={{ height: `${Math.max(2, (s.revenue_cents / maxRev) * 100)}%` }} />
            </div>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
          <span>{data.series[0]?.date?.slice(5)}</span><span>{data.series[data.series.length - 1]?.date?.slice(5)}</span>
        </div>
      </Card>

      {/* funnel */}
      <Card title="Autonomous funnel">
        <div className="space-y-2" data-testid="revenue-funnel">
          {funnelSteps.map((s) => {
            const v = f[s.k] || 0;
            return (
              <div key={s.k} className="flex items-center gap-3">
                <span className="flex w-40 shrink-0 items-center gap-2 text-sm text-muted-foreground"><s.icon size={15} /> {s.label}</span>
                <div className="h-6 flex-1 overflow-hidden rounded-md bg-white/5">
                  <div className="flex h-full items-center rounded-md bg-primary/60 px-2 text-xs font-semibold text-white"
                    style={{ width: `${Math.max(6, (v / maxF) * 100)}%` }}>{v}</div>
                </div>
              </div>
            );
          })}
          <div className="flex gap-3 pt-1 text-sm">
            <span className="flex items-center gap-1 text-emerald-500"><CheckCircle size={15} weight="fill" /> Won {f.deals_won}</span>
            <span className="flex items-center gap-1 text-muted-foreground"><XCircle size={15} weight="fill" /> Lost {f.deals_lost}</span>
          </div>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Product revenue">
          <Table testid="revenue-products" cols={["Product", "Sales", "Revenue"]}
            rows={(r.product_revenue || []).map((p) => [p.product?.replace(/_/g, " "), p.count, cents(p.cents)])}
            empty="No sales recorded yet" />
        </Card>
        <Card title="Channel performance">
          <Table testid="revenue-channels" cols={["Channel", "Sent", "Converted"]}
            rows={(data.channels || []).map((c) => [c.channel, c.sent, c.converted])}
            empty="No outreach sent yet" />
        </Card>
        <Card title="Source performance">
          <Table testid="revenue-sources" cols={["Source", "Leads", "Qualified", "Customers"]}
            rows={(data.sources || []).map((s) => [s.source, s.leads, s.qualified, s.customers])}
            empty="No leads yet" />
        </Card>
        <Card title="Campaign performance">
          <Table testid="revenue-campaigns" cols={["Campaign", "Leads", "Qualified"]}
            rows={(data.campaigns || []).map((c) => [c.name, c.leads, c.qualified])}
            empty="No campaigns yet" />
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Mini label="Cost / lead" value={cents(r.cost_per_lead_cents)} hint={r.spend_cents ? "" : "set campaign spend"} />
        <Mini label="Cost / sale" value={cents(r.cost_per_sale_cents)} hint={r.spend_cents ? "" : "set campaign spend"} />
        <Mini label="Follow-ups due" value={data.ops.followups_due} />
        <Mini label="Follow-ups sent" value={data.ops.followups_sent} />
        <Mini label="Delivery failures" value={data.ops.outreach_failed ?? 0} icon={XCircle} />
        <Mini label="Human escalations" value={data.ops.human_escalations} icon={HandWaving} />
      </div>
    </div>
  );
}

const Big = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-card p-4">
    <p className={`font-display text-2xl font-semibold ${accent ? "text-emerald-500" : ""}`}>{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);
const Mini = ({ label, value, hint, icon: Icon }) => (
  <div className="rounded-2xl border border-white/10 bg-card p-3">
    <p className="flex items-center gap-1.5 font-display text-lg font-semibold">{Icon && <Icon size={16} />}{value}</p>
    <p className="text-xs text-muted-foreground">{label}{hint ? ` · ${hint}` : ""}</p>
  </div>
);
const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-white/10 bg-card p-4">
    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
    {children}
  </div>
);
const Table = ({ cols, rows, empty, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    {rows.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p> : (
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
          {cols.map((c) => <th key={c} className="pb-2 font-medium">{c}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-white/5">
              {row.map((cell, j) => <td key={j} className={`py-2 ${j === 0 ? "font-medium capitalize" : "text-muted-foreground"}`}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);
